import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { useChurchPlanner } from '../../hooks/useChurchPlanner';
import {
  PlannerPage,
  PlannerCard,
  PlannerKicker,
  PlannerHeading,
  PlannerSubtle,
  PlannerError,
  plannerColors,
} from './plannerTheme';
import { formatPlannerStatusLabel, normalizePlannerStatus } from './plannerStatus';

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const Item = styled.div`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  padding: 0.85rem 0.95rem;
  background: ${plannerColors.surfaceHover};
`;

const ActionRow = styled.div`
  display: flex;
  gap: 0.45rem;
  margin-top: 0.45rem;
`;

const ActionButton = styled.button<{ $tone: 'ok' | 'bad'; $active?: boolean }>`
  border: 1px solid
    ${({ $tone, $active }) =>
      $active
        ? $tone === 'ok'
          ? 'rgba(52, 199, 89, 0.58)'
          : 'rgba(255, 69, 58, 0.58)'
        : plannerColors.border};
  border-radius: 999px;
  padding: 0.26rem 0.6rem;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  color: ${({ $tone, $active }) =>
    $active
      ? $tone === 'ok'
        ? '#102113'
        : '#2c0f0e'
      : plannerColors.text};
  background: ${({ $tone, $active }) =>
    $active
      ? $tone === 'ok'
        ? 'rgba(52, 199, 89, 0.75)'
        : 'rgba(255, 69, 58, 0.76)'
      : plannerColors.surface};
  opacity: ${({ disabled }) => (disabled ? 0.55 : 1)};
`;

function formatDateTime(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getRoleName(entryRole: { name: string } | Array<{ name: string }> | null | undefined) {
  if (!entryRole) return null;
  if (Array.isArray(entryRole)) return entryRole[0]?.name ?? null;
  return entryRole.name ?? null;
}

export default function ChurchPlannerMyAssignmentsPage() {
  const { user } = useAuth();
  const planner = useChurchPlanner(true, user?.id);
  const [updatingAssignmentId, setUpdatingAssignmentId] = useState<string | null>(null);

  const handleSetAssignmentStatus = async (
    entry: { assignment: { id: string; status?: string | null }; plan: { id: string } },
    status: 'confirmed' | 'declined'
  ) => {
    if (!user?.id) {
      window.alert('Please sign in before setting availability.');
      return;
    }

    const assignmentId = entry.assignment.id;
    setUpdatingAssignmentId(assignmentId);
    try {
      await planner.respondToServicePlan({
        service_plan_id: entry.plan.id,
        user_id: user.id,
        response_status: status,
        notes: null,
        updated_by: user.id,
      });
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to update assignment status.');
    } finally {
      setUpdatingAssignmentId(null);
    }
  };

  if (planner.isLoading) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerSubtle>Loading assignments...</PlannerSubtle>
        </PlannerCard>
      </PlannerPage>
    );
  }

  if (planner.isError) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerError>Unable to load your assignments right now.</PlannerError>
        </PlannerCard>
      </PlannerPage>
    );
  }

  return (
    <PlannerPage>
      <PlannerCard>
        <PlannerKicker>Serving</PlannerKicker>
        <PlannerHeading>My Assignments</PlannerHeading>
        <PlannerSubtle>
          View your assignments across all services, including upcoming and history.
        </PlannerSubtle>
      </PlannerCard>

      <PlannerCard>
        <PlannerHeading style={{ fontSize: '1.1rem' }}>Upcoming services</PlannerHeading>
        {planner.myUpcomingAssignments.length === 0 ? (
          <PlannerSubtle>No upcoming assignments found.</PlannerSubtle>
        ) : (
          <List>
            {planner.myUpcomingAssignments.map((entry) => {
              const normalizedStatus = normalizePlannerStatus(entry.assignment.status);
              const isConfirmed = normalizedStatus === 'confirmed';
              return (
                <Item key={entry.assignment.id}>
                  <PlannerSubtle style={{ margin: 0 }}>{entry.plan.title}</PlannerSubtle>
                  <PlannerSubtle>{formatDateTime(entry.plan.service_date)}</PlannerSubtle>
                  <PlannerSubtle>
                    Role: {getRoleName(entry.assignment.service_plan_roles) || 'Unassigned role'}
                  </PlannerSubtle>
                  <PlannerSubtle>Status: {formatPlannerStatusLabel(normalizedStatus)}</PlannerSubtle>
                  <ActionRow>
                    <ActionButton
                      $tone="ok"
                      $active={normalizedStatus === 'confirmed'}
                      onClick={() => void handleSetAssignmentStatus(entry, 'confirmed')}
                      disabled={updatingAssignmentId === entry.assignment.id}
                    >
                      {updatingAssignmentId === entry.assignment.id ? 'Saving...' : isConfirmed ? 'CONFIRMED' : 'Confirm'}
                    </ActionButton>
                    {!isConfirmed ? (
                      <ActionButton
                        $tone="bad"
                        $active={normalizedStatus === 'unavailable'}
                        onClick={() => void handleSetAssignmentStatus(entry, 'declined')}
                        disabled={updatingAssignmentId === entry.assignment.id}
                      >
                        MARK NOT AVAILABLE
                      </ActionButton>
                    ) : null}
                  </ActionRow>
                </Item>
              );
            })}
          </List>
        )}
      </PlannerCard>

      <PlannerCard>
        <PlannerHeading style={{ fontSize: '1.1rem' }}>Serving history</PlannerHeading>
        {planner.myHistoryAssignments.length === 0 ? (
          <PlannerSubtle>No service history found.</PlannerSubtle>
        ) : (
          <List>
            {planner.myHistoryAssignments.map((entry) => (
              <Item key={entry.assignment.id}>
                <PlannerSubtle style={{ margin: 0 }}>{entry.plan.title}</PlannerSubtle>
                <PlannerSubtle>{formatDateTime(entry.plan.service_date)}</PlannerSubtle>
                <PlannerSubtle>
                  Role: {getRoleName(entry.assignment.service_plan_roles) || 'Unassigned role'}
                </PlannerSubtle>
                <PlannerSubtle>Status: {formatPlannerStatusLabel(normalizePlannerStatus(entry.assignment.status))}</PlannerSubtle>
              </Item>
            ))}
          </List>
        )}
      </PlannerCard>
    </PlannerPage>
  );
}
